import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { openModal } from 'actions/modal';
import { showToast } from 'actions/toast-msg';
import { MODAL_FORM, MODAL_CONFIRMATION_ACTIONS } from 'constants/form';

// Outcomes and confirmations for the queue. Announced globally, so feedback finds you even
// when the row you acted on is 2000px below where the message used to appear inline.
const useFeedback = () => {
  const dispatch = useDispatch();

  const toast = useCallback(message => dispatch(showToast(message, null, 5000)), [
    dispatch,
  ]);

  const confirmThen = useCallback(
    (title, description, onConfirm) =>
      dispatch(
        openModal(
          MODAL_FORM.CONFIRMATION,
          { blocking: true, transparent: false },
          {
            title,
            description,
            onConfirm: action => {
              if (action === MODAL_CONFIRMATION_ACTIONS.CONFIRMED) onConfirm();
            },
          },
        ),
      ),
    [dispatch],
  );

  const copyValue = useCallback(
    (label, value) => {
      if (!value || !navigator.clipboard) return;
      // A denied clipboard permission rejects; swallow it rather than surface an
      // unhandled rejection.
      navigator.clipboard.writeText(value).catch(() => {});
      toast(`${label} copied`);
    },
    [toast],
  );

  // The completion handler every record mutation shares: say what happened, and on success
  // hand back to the caller for whatever it has to put away.
  const reportOutcome = useCallback(
    (verb, onSuccess) => error => {
      if (error) {
        toast(`${verb} failed: ${error}`);
        return;
      }
      toast(`${verb} ok`);
      onSuccess();
    },
    [toast],
  );

  return { toast, confirmThen, copyValue, reportOutcome };
};

export default useFeedback;
